/**
 * StepDetailPanel: reusable step timeline + action bar for a single email's pipeline steps.
 *
 * Receives an emailId from the parent (e.g. selected in the email list) and displays
 * the 22-step pipeline grouped by phase, with expand/collapse I/O panels, status badges,
 * and re-run controls (Run All, Step by Step, Stop).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    CheckCircle,
    XCircle,
    RefreshCw05,
    ChevronDown,
    ChevronRight,
    Clock,
    AlertTriangle,
    Square,
} from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Badge } from "@/components/base/badges/badges";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import { storage } from "@/lib/local-storage";
import { useDebugStream, type StepInspectorEvent } from "@/hooks/api/use-debug-stream";

// ============================================================================
// Types (exported for reuse)
// ============================================================================

export interface StepStatus {
    step_name: string;
    phase: string;
    status: "completed" | "failed" | "not_run" | "skipped" | "running";
    step_type?: string;
    duration_ms: number;
    error?: string;
    input_snapshot?: Record<string, unknown>;
    output_snapshot?: Record<string, unknown>;
    model_function_key?: string | null;
    current_model?: string | null;
}

export interface PipelineStatusResponse {
    email_id: string;
    subject?: string;
    sender?: string;
    received_at?: string;
    body_preview?: string;
    attachment_names?: string[];
    status: string;
    steps: StepStatus[];
}

export interface StepDetailPanelProps {
    emailId: string;
    companyId: string;
    onStepRerun?: (stepName: string) => void;
    onStatusChange?: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const PHASE_ORDER = [
    "Email Processing",
    "Company & Contacts",
    "Container Resolution",
    "Independent Enrichment",
    "Validation Chain",
];

const PHASE_LABELS: Record<string, string> = {
    email_processing: "Email Processing",
    company_contacts: "Company & Contacts",
    container_resolution: "Container Resolution",
    independent_enrichment: "Independent Enrichment",
    validation_chain: "Validation Chain",
};

const LLM_MODEL_OPTIONS = [
    { id: "anthropic/claude-haiku-4-5-latest", label: "Claude Haiku 4.5" },
    { id: "anthropic/claude-sonnet-4-5-latest", label: "Claude Sonnet 4.5" },
    { id: "anthropic/claude-sonnet-4-latest", label: "Claude Sonnet 4" },
];

// ============================================================================
// Helpers (exported for potential reuse)
// ============================================================================

export function getStatusColor(status: string): "success" | "error" | "gray" | "blue-light" | "brand" {
    switch (status) {
        case "completed":
            return "success";
        case "failed":
            return "error";
        case "skipped":
            return "blue-light";
        case "running":
            return "brand";
        default:
            return "gray";
    }
}

export function getStatusLabel(status: string): string {
    switch (status) {
        case "completed":
            return "Completed";
        case "failed":
            return "Failed";
        case "skipped":
            return "Skipped";
        case "not_run":
            return "Not Run";
        case "running":
            return "Running";
        default:
            return status;
    }
}

export function formatDuration(ms: number): string {
    if (ms <= 0) return "-";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

function normalizePhase(phase: string): string {
    return PHASE_LABELS[phase] || phase;
}

function groupStepsByPhase(steps: StepStatus[]): Map<string, StepStatus[]> {
    const grouped = new Map<string, StepStatus[]>();

    for (const step of steps) {
        const phase = normalizePhase(step.phase);
        if (!grouped.has(phase)) {
            grouped.set(phase, []);
        }
        grouped.get(phase)!.push(step);
    }

    // Sort by defined phase order, then any remaining phases
    const sorted = new Map<string, StepStatus[]>();
    for (const phase of PHASE_ORDER) {
        if (grouped.has(phase)) {
            sorted.set(phase, grouped.get(phase)!);
            grouped.delete(phase);
        }
    }
    // Append any phases not in PHASE_ORDER
    for (const [phase, steps] of grouped) {
        sorted.set(phase, steps);
    }

    return sorted;
}

/** Flatten grouped steps into an ordered list of step names */
function getOrderedStepNames(steps: StepStatus[]): string[] {
    const grouped = groupStepsByPhase(steps);
    const names: string[] = [];
    for (const phaseSteps of grouped.values()) {
        for (const step of phaseSteps) {
            names.push(step.step_name);
        }
    }
    return names;
}

// ============================================================================
// Sub-components
// ============================================================================

function JsonPanel({ title, data }: { title: string; data?: Record<string, unknown> | null }) {
    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-tertiary mb-1">{title}</p>
                <div className="rounded border border-secondary bg-gray-950 p-3 text-xs text-gray-500 italic font-mono">
                    No data
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-tertiary mb-1">{title}</p>
            <pre className="rounded border border-secondary bg-gray-950 p-3 text-xs text-green-400 font-mono overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all">
                {JSON.stringify(data, null, 2)}
            </pre>
        </div>
    );
}

function ModelSelect({
    functionKey,
    currentModel,
    selectedModel,
    onChange,
}: {
    functionKey: string;
    currentModel?: string | null;
    selectedModel?: string;
    onChange: (functionKey: string, modelId: string) => void;
}) {
    const value = selectedModel || currentModel || "";
    return (
        <select
            className="text-xs bg-secondary border border-tertiary rounded px-1.5 py-0.5 text-primary max-w-[10rem] truncate"
            value={value}
            onChange={(e) => {
                e.stopPropagation();
                onChange(functionKey, e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            title={`Model for ${functionKey}`}
        >
            {LLM_MODEL_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                    {opt.label}
                </option>
            ))}
        </select>
    );
}

function StepCard({
    step,
    isExpanded,
    onToggle,
    isNextStep,
    modelOverride,
    onModelChange,
}: {
    step: StepStatus;
    isExpanded: boolean;
    onToggle: () => void;
    isNextStep?: boolean;
    modelOverride?: string;
    onModelChange?: (functionKey: string, modelId: string) => void;
}) {
    const statusColor = getStatusColor(step.status);
    const hasDetail = step.input_snapshot || step.output_snapshot;
    const showModelSelect = step.model_function_key && onModelChange;

    return (
        <div
            data-debug-step={step.step_name}
            className={`rounded-lg border overflow-hidden ${
            isNextStep
                ? "border-brand-solid ring-1 ring-brand-solid/30"
                : "border-secondary"
        } bg-primary`}>
            {/* Collapsed header */}
            <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/50 transition-colors"
                onClick={onToggle}
                disabled={!hasDetail && step.status === "not_run"}
            >
                {hasDetail || step.status !== "not_run" ? (
                    isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-tertiary shrink-0" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-tertiary shrink-0" />
                    )
                ) : (
                    <div className="h-4 w-4 shrink-0" />
                )}

                <span className="text-sm font-medium text-primary flex-1 truncate">
                    {step.step_name}
                </span>

                {showModelSelect && (
                    <ModelSelect
                        functionKey={step.model_function_key!}
                        currentModel={step.current_model}
                        selectedModel={modelOverride}
                        onChange={onModelChange!}
                    />
                )}

                {step.status === "running" ? (
                    <Badge color="brand" size="sm">
                        <RefreshCw05 className="h-3 w-3 animate-spin" />
                        Running
                    </Badge>
                ) : (
                    <Badge color={statusColor} size="sm">
                        {getStatusLabel(step.status)}
                    </Badge>
                )}

                <span className="text-xs text-tertiary font-mono tabular-nums w-16 text-right shrink-0">
                    {formatDuration(step.duration_ms)}
                </span>
            </button>

            {/* Error message visible without expanding */}
            {step.status === "failed" && step.error && !isExpanded && (
                <div className="px-4 pb-3 -mt-1">
                    <div className="flex items-start gap-2 text-xs text-error-primary">
                        <XCircle className="h-3 w-3 shrink-0 mt-0.5" />
                        <span className="break-all">{step.error}</span>
                    </div>
                </div>
            )}

            {/* Expanded detail */}
            {isExpanded && (
                <div className="border-t border-secondary px-4 py-3 space-y-3">
                    {step.status === "failed" && step.error && (
                        <div className="flex items-start gap-2 text-sm text-error-primary">
                            <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span className="break-all">{step.error}</span>
                        </div>
                    )}
                    <div className="flex gap-4 flex-col sm:flex-row">
                        <JsonPanel title="Input" data={step.input_snapshot} />
                        <JsonPanel title="Output" data={step.output_snapshot} />
                    </div>
                </div>
            )}
        </div>
    );
}

function StepTimeline({
    steps,
    expandedSteps,
    onToggleStep,
    nextStepName,
    modelOverrides,
    onModelChange,
}: {
    steps: StepStatus[];
    expandedSteps: Set<string>;
    onToggleStep: (stepName: string) => void;
    nextStepName?: string;
    modelOverrides?: Record<string, string>;
    onModelChange?: (functionKey: string, modelId: string) => void;
}) {
    const grouped = useMemo(() => groupStepsByPhase(steps), [steps]);

    if (steps.length === 0) {
        return (
            <div className="rounded-lg border border-secondary bg-primary p-8 text-center">
                <p className="text-tertiary text-sm">No pipeline steps found for this email.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {Array.from(grouped.entries()).map(([phase, phaseSteps]) => (
                <div key={phase} className="space-y-2">
                    <h4 className="text-xs font-semibold text-tertiary uppercase tracking-wide">
                        {phase}
                    </h4>
                    <div className="space-y-1.5">
                        {phaseSteps.map((step) => (
                            <StepCard
                                key={step.step_name}
                                step={step}
                                isExpanded={expandedSteps.has(step.step_name)}
                                onToggle={() => onToggleStep(step.step_name)}
                                isNextStep={step.step_name === nextStepName}
                                modelOverride={step.model_function_key ? modelOverrides?.[step.model_function_key] : undefined}
                                onModelChange={onModelChange}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ============================================================================
// Main component
// ============================================================================

type RunMode = "idle" | "all" | "stepping";

export function StepDetailPanel({ emailId, onStatusChange }: StepDetailPanelProps) {
    // Expanded steps
    const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
    // Email viewer collapsed by default
    const [emailViewerOpen, setEmailViewerOpen] = useState(false);

    // Action bar state
    const [dryRun, setDryRun] = useState(true);
    const [documentId, setDocumentId] = useState("");
    const [runError, setRunError] = useState<string | null>(null);

    // Step-by-step mode
    const [runMode, setRunMode] = useState<RunMode>("idle");
    const [stepIndex, setStepIndex] = useState(0);
    const [autoAdvance, setAutoAdvance] = useState(false);
    const prevEmailIdRef = useRef(emailId);

    // Per-step LLM model overrides (function_key -> model_id)
    const [modelOverrides, setModelOverrides] = useState<Record<string, string>>({});

    // SSE stream for re-runs
    const { isRunning, connectStepInspector, stepEvents, clearStepEvents, disconnect } = useDebugStream();

    // Fetch pipeline status for the loaded email
    const {
        data: pipelineStatus,
        isLoading: isLoadingStatus,
        error: statusError,
        refetch: refetchStatus,
    } = useQuery({
        queryKey: ["admin", "debug", "pipeline-steps", emailId],
        queryFn: async (): Promise<PipelineStatusResponse> => {
            const raw = await apiGet<Record<string, unknown>>(
                `/admin/debug/pipeline-steps/${emailId}/status`
            );
            // Backend returns groups[].steps[], flatten to steps[]
            const groups = (raw.groups ?? []) as Array<{ steps: StepStatus[] }>;
            const steps = groups.flatMap((g) => g.steps ?? []);
            return {
                email_id: raw.email_id as string,
                subject: raw.subject as string | undefined,
                sender: raw.sender as string | undefined,
                received_at: raw.received_at as string | undefined,
                body_preview: raw.body_preview as string | undefined,
                attachment_names: raw.attachment_names as string[] | undefined,
                status: raw.status as string ?? "unknown",
                steps,
            };
        },
        enabled: !!emailId,
    });

    // Reset state when email changes
    useEffect(() => {
        if (emailId !== prevEmailIdRef.current) {
            prevEmailIdRef.current = emailId;
            setExpandedSteps(new Set());
            setRunError(null);
            setRunMode("idle");
            setStepIndex(0);
            setAutoAdvance(false);
            setModelOverrides({});
            disconnect();
        }
    }, [emailId, disconnect]);

    // Merge SSE step events into the static steps to show live updates
    const mergedSteps = useMemo((): StepStatus[] => {
        const base = pipelineStatus?.steps ?? [];
        if (stepEvents.length === 0) return base;

        // Build a map of updates from SSE events
        const updates = new Map<string, Partial<StepStatus>>();
        for (const evt of stepEvents) {
            if (!evt.step_name) continue;
            const existing = updates.get(evt.step_name) ?? {};

            if (evt.event === "step_start") {
                updates.set(evt.step_name, {
                    ...existing,
                    status: "running",
                    phase: evt.phase ?? evt.group ?? existing.phase ?? "",
                });
            } else if (evt.event === "step_complete") {
                updates.set(evt.step_name, {
                    ...existing,
                    status: "completed",
                    duration_ms: evt.duration_ms ?? 0,
                    input_snapshot: evt.input_snapshot ?? evt.input ?? existing.input_snapshot,
                    output_snapshot: evt.output_snapshot ?? evt.output ?? existing.output_snapshot,
                    phase: evt.phase ?? evt.group ?? existing.phase ?? "",
                });
            } else if (evt.event === "step_error") {
                updates.set(evt.step_name, {
                    ...existing,
                    status: "failed",
                    error: evt.error ?? evt.traceback,
                    duration_ms: evt.duration_ms ?? 0,
                    input_snapshot: evt.input_snapshot ?? evt.input ?? existing.input_snapshot,
                    phase: evt.phase ?? evt.group ?? existing.phase ?? "",
                });
            }
        }

        if (updates.size === 0) return base;

        // Merge updates into base steps
        const stepMap = new Map<string, StepStatus>();
        for (const step of base) {
            stepMap.set(step.step_name, { ...step });
        }
        for (const [name, update] of updates) {
            const existing = stepMap.get(name);
            if (existing) {
                stepMap.set(name, { ...existing, ...update } as StepStatus);
            } else {
                stepMap.set(name, {
                    step_name: name,
                    phase: update.phase ?? "unknown",
                    status: update.status ?? "not_run",
                    duration_ms: update.duration_ms ?? 0,
                    error: update.error,
                    input_snapshot: update.input_snapshot,
                    output_snapshot: update.output_snapshot,
                } as StepStatus);
            }
        }

        return Array.from(stepMap.values());
    }, [pipelineStatus?.steps, stepEvents]);

    // Ordered step names for step-by-step navigation
    const orderedStepNames = useMemo(
        () => getOrderedStepNames(mergedSteps),
        [mergedSteps]
    );

    // Check if run completed from SSE events
    const runCompleteEvent = useMemo(
        () => stepEvents.find((e) => e.event === "run_complete"),
        [stepEvents]
    );

    // When stepping completes a step, auto-advance if autoAdvance flag is on
    useEffect(() => {
        if (runMode !== "stepping" || isRunning) return;

        // A step just completed, advance index
        const lastComplete = stepEvents.filter(
            (e) => e.event === "step_complete" || e.event === "step_error"
        );
        if (lastComplete.length === 0) return;

        // Check if we've done the current step
        const currentStepName = orderedStepNames[stepIndex];
        const isDone = stepEvents.some(
            (e) =>
                e.step_name === currentStepName &&
                (e.event === "step_complete" || e.event === "step_error")
        );

        if (isDone) {
            const nextIdx = stepIndex + 1;
            if (nextIdx < orderedStepNames.length) {
                setStepIndex(nextIdx);
                if (autoAdvance) {
                    // Auto-advance: run next step
                    runSingleStep(orderedStepNames[nextIdx]);
                }
            } else {
                // All steps done
                setRunMode("idle");
                setAutoAdvance(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepEvents, isRunning, runMode, stepIndex, autoAdvance, orderedStepNames]);

    // Compute the "next step" name for highlighting in the timeline
    const nextStepName = useMemo(() => {
        if (runMode === "stepping" && stepIndex < orderedStepNames.length) {
            return orderedStepNames[stepIndex];
        }
        return undefined;
    }, [runMode, stepIndex, orderedStepNames]);

    /** Step currently executing (last step_start without matching complete/error). */
    const activeRunningStepName = useMemo(() => {
        let current: string | null = null;
        for (const e of stepEvents) {
            if (e.event === "step_start" && e.step_name) {
                current = e.step_name;
            }
            if (
                (e.event === "step_complete" || e.event === "step_error") &&
                e.step_name &&
                e.step_name === current
            ) {
                current = null;
            }
        }
        return current;
    }, [stepEvents]);

    const rerunProgress = useMemo(() => {
        const totalSteps = orderedStepNames.length;
        const finished = stepEvents.filter(
            (e) => e.event === "step_complete" || e.event === "step_error"
        ).length;
        const pct =
            totalSteps > 0 ? Math.min(100, Math.round((finished / totalSteps) * 100)) : 0;
        return { totalSteps, finished, pct };
    }, [stepEvents, orderedStepNames.length]);

    useEffect(() => {
        if (!activeRunningStepName || !isRunning) return;
        const el = document.querySelector(
            `[data-debug-step="${CSS.escape(activeRunningStepName)}"]`
        );
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [activeRunningStepName, isRunning]);

    // Handlers
    const handleToggleStep = useCallback((stepName: string) => {
        setExpandedSteps((prev) => {
            const next = new Set(prev);
            if (next.has(stepName)) {
                next.delete(stepName);
            } else {
                next.add(stepName);
            }
            return next;
        });
    }, []);

    const handleModelChange = useCallback((functionKey: string, modelId: string) => {
        setModelOverrides((prev) => ({ ...prev, [functionKey]: modelId }));
    }, []);

    const runSingleStep = useCallback(
        (stepName: string) => {
            if (!emailId) return;
            const token = storage.getItem("access_token");
            if (!token) {
                setRunError("No auth token found. Please log in again.");
                return;
            }
            setRunError(null);
            const body: Record<string, unknown> = {
                dry_run: dryRun,
                steps: [stepName],
            };
            if (documentId.trim()) {
                body.document_id = documentId.trim();
            }
            // Include model overrides if any are set
            if (Object.keys(modelOverrides).length > 0) {
                body.model_overrides = modelOverrides;
            }
            connectStepInspector(emailId, token, body);
        },
        [emailId, dryRun, documentId, modelOverrides, connectStepInspector]
    );

    const handleRunAll = useCallback(() => {
        if (!emailId) return;
        const token = storage.getItem("access_token");
        if (!token) {
            setRunError("No auth token found. Please log in again.");
            return;
        }
        setRunError(null);
        setRunMode("all");
        setAutoAdvance(false);

        const body: Record<string, unknown> = {
            dry_run: dryRun,
            mode: "all",
        };
        if (documentId.trim()) {
            body.document_id = documentId.trim();
        }
        connectStepInspector(emailId, token, body);
    }, [emailId, dryRun, documentId, connectStepInspector]);

    const handleStepByStep = useCallback(() => {
        if (orderedStepNames.length === 0) return;

        // Find first not_run step
        let startIdx = 0;
        for (let i = 0; i < orderedStepNames.length; i++) {
            const step = mergedSteps.find((s) => s.step_name === orderedStepNames[i]);
            if (step && step.status === "not_run") {
                startIdx = i;
                break;
            }
        }

        setRunMode("stepping");
        setStepIndex(startIdx);
        setAutoAdvance(false);
        runSingleStep(orderedStepNames[startIdx]);
    }, [orderedStepNames, mergedSteps, runSingleStep]);

    const handleNextStep = useCallback(() => {
        if (stepIndex >= orderedStepNames.length) return;
        runSingleStep(orderedStepNames[stepIndex]);
    }, [stepIndex, orderedStepNames, runSingleStep]);

    const handleContinueAuto = useCallback(() => {
        setAutoAdvance(true);
        // If not currently running, kick off next step
        if (!isRunning && stepIndex < orderedStepNames.length) {
            runSingleStep(orderedStepNames[stepIndex]);
        }
    }, [isRunning, stepIndex, orderedStepNames, runSingleStep]);

    const handleStop = useCallback(() => {
        disconnect();
        setRunMode("idle");
        setAutoAdvance(false);
    }, [disconnect]);

    // Discard SSE overlay and show raw DB state
    const handleDiscard = useCallback(() => {
        clearStepEvents();
        refetchStatus();
    }, [clearStepEvents, refetchStatus]);

    // Count steps by status
    const statusCounts = useMemo(() => {
        const counts = { completed: 0, failed: 0, skipped: 0, not_run: 0, running: 0 };
        for (const step of mergedSteps) {
            if (step.status in counts) {
                counts[step.status as keyof typeof counts]++;
            }
        }
        return counts;
    }, [mergedSteps]);

    // When a non-dry-run finishes, refetch to get persisted results
    // In dry-run mode, DON'T refetch — it would overwrite SSE merge results
    // (SAVEPOINT rollback means API still returns "not_run")
    const lastHandledCompleteRef = useRef<StepInspectorEvent | null>(null);
    useEffect(() => {
        if (runCompleteEvent && !isRunning && runCompleteEvent !== lastHandledCompleteRef.current) {
            lastHandledCompleteRef.current = runCompleteEvent;
            if (!dryRun) {
                refetchStatus();
                onStatusChange?.();
            }
            if (runMode === "all") {
                setRunMode("idle");
            }
        }
    }, [runCompleteEvent, isRunning, runMode, dryRun, refetchStatus, onStatusChange]);

    if (!emailId) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <p className="text-tertiary text-sm">Select an email from the list to view its pipeline steps.</p>
            </div>
        );
    }

    if (isLoadingStatus) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="flex items-center gap-2 text-sm text-tertiary">
                    <RefreshCw05 className="h-4 w-4 animate-spin" />
                    Loading pipeline status...
                </div>
            </div>
        );
    }

    if (statusError) {
        return (
            <div className="flex-1 p-4">
                <div className="flex items-center gap-2 text-sm text-error-primary">
                    <XCircle className="h-4 w-4 shrink-0" />
                    {statusError instanceof Error ? statusError.message : "Failed to load pipeline status"}
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Status summary bar */}
            {mergedSteps.length > 0 && (
                <div className="flex items-center gap-4 text-sm px-1 pb-3 shrink-0">
                    {pipelineStatus?.subject && (
                        <span className="text-secondary truncate max-w-md" title={pipelineStatus.subject}>
                            &ldquo;{pipelineStatus.subject}&rdquo;
                        </span>
                    )}
                    {pipelineStatus?.status && (
                        <Badge color={pipelineStatus.status === "processed" ? "success" : pipelineStatus.status === "pending" ? "warning" : "gray"} size="sm">
                            {pipelineStatus.status}
                        </Badge>
                    )}
                    <div className="flex items-center gap-3 ml-auto">
                        {statusCounts.completed > 0 && (
                            <span className="flex items-center gap-1">
                                <CheckCircle className="h-3.5 w-3.5 text-success-primary" />
                                <span className="text-tertiary">{statusCounts.completed}</span>
                            </span>
                        )}
                        {statusCounts.failed > 0 && (
                            <span className="flex items-center gap-1">
                                <XCircle className="h-3.5 w-3.5 text-error-primary" />
                                <span className="text-tertiary">{statusCounts.failed}</span>
                            </span>
                        )}
                        {statusCounts.skipped > 0 && (
                            <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5 text-blue-400" />
                                <span className="text-tertiary">{statusCounts.skipped}</span>
                            </span>
                        )}
                        {statusCounts.not_run > 0 && (
                            <span className="flex items-center gap-1">
                                <span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-400" />
                                <span className="text-tertiary">{statusCounts.not_run}</span>
                            </span>
                        )}
                        {(isRunning || runMode !== "idle") && (
                            <Badge color="brand" size="sm">
                                <RefreshCw05 className="h-3 w-3 animate-spin" />
                                {runMode === "stepping" ? "Step by Step" : "Running"}
                            </Badge>
                        )}
                    </div>
                </div>
            )}

            {/* Action bar — TOP of panel for visibility */}
            <div className="border-b border-secondary pb-3 mb-1 space-y-2 shrink-0">
                <div className="flex flex-wrap items-center gap-2">
                    {/* Dry-run badge — always visible, clickable to toggle */}
                    <button
                        type="button"
                        onClick={() => !isRunning && setDryRun((v) => !v)}
                        disabled={isRunning}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                            dryRun
                                ? "border-brand-solid/40 bg-brand-primary_alt text-brand-secondary"
                                : "border-warning/40 bg-warning/10 text-warning"
                        } ${isRunning ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:opacity-80"}`}
                    >
                        {dryRun ? "Dry Run" : "Live Mode"}
                    </button>

                    <div className="w-px h-5 bg-secondary" />

                    <Button
                        color="primary"
                        size="sm"
                        onPress={handleRunAll}
                        isDisabled={isRunning || mergedSteps.length === 0}
                    >
                        Run All
                    </Button>

                    {runMode !== "stepping" ? (
                        <Button
                            color="secondary"
                            size="sm"
                            onPress={handleStepByStep}
                            isDisabled={isRunning || mergedSteps.length === 0}
                        >
                            Step by Step
                        </Button>
                    ) : (
                        <>
                            <Button
                                color="secondary"
                                size="sm"
                                onPress={handleNextStep}
                                isDisabled={isRunning || stepIndex >= orderedStepNames.length}
                            >
                                Next Step ({stepIndex + 1}/{orderedStepNames.length})
                            </Button>
                            <Button
                                color="secondary"
                                size="sm"
                                onPress={handleContinueAuto}
                                isDisabled={isRunning || autoAdvance || stepIndex >= orderedStepNames.length}
                            >
                                Continue Auto
                            </Button>
                        </>
                    )}

                    {(isRunning || runMode !== "idle") && (
                        <Button
                            color="secondary-destructive"
                            size="sm"
                            onPress={handleStop}
                        >
                            <span className="inline-flex items-center gap-1.5"><Square className="h-3.5 w-3.5 shrink-0" /> Stop</span>
                        </Button>
                    )}

                    <div className="ml-auto">
                        <Input
                            placeholder="Document ID (post-extraction)..."
                            value={documentId}
                            onChange={(v) => setDocumentId(v)}
                            isDisabled={isRunning}
                            size="sm"
                        />
                    </div>
                </div>

                {!dryRun && runMode === "idle" && (
                    <div className="flex items-center gap-2 text-xs text-warning">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        Live mode: changes will be saved to the database.
                    </div>
                )}

                {runError && (
                    <div className="flex items-center gap-2 text-sm text-error-primary">
                        <XCircle className="h-4 w-4 shrink-0" />
                        {runError}
                    </div>
                )}
            </div>

            {/* Run complete banner — visible without scrolling */}
            {runCompleteEvent && !isRunning && runMode === "idle" && (
                <div className="rounded-lg border border-secondary bg-primary p-4 space-y-3 mb-3 shrink-0">
                    {(() => {
                        const rcFailed = runCompleteEvent.failed ?? 0;
                        const rcCompleted = runCompleteEvent.completed ?? 0;
                        const rcTotal = runCompleteEvent.total ?? 0;
                        const hasFailures = rcFailed > 0;
                        return (
                    <div className="flex items-start gap-2 text-sm">
                        {hasFailures ? (
                            <AlertTriangle className="h-4 w-4 text-warning-primary shrink-0 mt-0.5" />
                        ) : (
                            <CheckCircle className="h-4 w-4 text-success-primary shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0">
                            <span className={`font-medium ${hasFailures ? "text-warning-primary" : "text-primary"}`}>
                                {hasFailures ? "Run finished with errors" : "Run completed"}
                            </span>
                            {(rcTotal > 0 || rcCompleted > 0 || rcFailed > 0) && (
                                <p className="text-tertiary text-xs mt-1">
                                    {rcCompleted} completed
                                    {rcFailed > 0 && (
                                        <span className="text-error-primary">, {rcFailed} failed</span>
                                    )}
                                    {rcTotal > 0 && (
                                        <span className="text-tertiary"> — {rcTotal} total</span>
                                    )}
                                </p>
                            )}
                            {runCompleteEvent.results &&
                                typeof runCompleteEvent.results === "object" && (
                                    <p className="text-tertiary text-xs mt-1 font-mono break-all">
                                        {Object.entries(runCompleteEvent.results)
                                            .map(([k, v]) => `${k}: ${v}`)
                                            .join(" | ")}
                                    </p>
                                )}
                        </div>
                    </div>
                        );
                    })()}

                    {dryRun ? (
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-secondary">Dry run complete — preview only, nothing was saved.</span>
                            <Button color="primary" size="sm" onPress={() => {
                                if (!emailId) return;
                                const token = storage.getItem("access_token");
                                if (!token) {
                                    setRunError("No auth token found. Please log in again.");
                                    return;
                                }
                                setRunError(null);
                                setDryRun(false);
                                setRunMode("all");
                                const body: Record<string, unknown> = { dry_run: false, mode: "all" };
                                if (documentId.trim()) body.document_id = documentId.trim();
                                connectStepInspector(emailId, token, body);
                            }}>
                                Run Live
                            </Button>
                            <Button color="secondary" size="sm" onPress={handleDiscard}>
                                Discard Preview
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-sm text-success-primary">
                            <CheckCircle className="h-4 w-4 shrink-0" />
                            Results saved to database
                        </div>
                    )}
                </div>
            )}

            {/* Re-run progress: mode, current step, bar */}
            {mergedSteps.length > 0 && (isRunning || runMode !== "idle") && (
                <div className="sticky top-0 z-20 shrink-0 border border-secondary rounded-lg bg-primary/95 backdrop-blur-sm px-3 py-2.5 mb-3 space-y-2 shadow-sm">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <Badge color="brand" size="sm">
                            {runMode === "stepping"
                                ? "Step by step"
                                : runMode === "all"
                                  ? "Run all"
                                  : "Re-run"}
                        </Badge>
                        {runMode === "stepping" && orderedStepNames.length > 0 && (
                            <span className="text-tertiary tabular-nums">
                                Position {Math.min(stepIndex + 1, orderedStepNames.length)} /{" "}
                                {orderedStepNames.length}
                            </span>
                        )}
                        {isRunning && (
                            <span className="flex items-center gap-1.5 min-w-0 flex-1">
                                <RefreshCw05 className="h-3.5 w-3.5 text-brand-secondary animate-spin shrink-0" />
                                <span className="text-secondary truncate">
                                    {activeRunningStepName
                                        ? `Running: ${activeRunningStepName}`
                                        : "Waiting…"}
                                </span>
                            </span>
                        )}
                        {!isRunning && runMode !== "idle" && (
                            <span className="text-tertiary text-xs">Paused or between steps</span>
                        )}
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                        <div
                            className="h-full rounded-full bg-brand-solid transition-all duration-300"
                            style={{ width: `${rerunProgress.pct}%` }}
                        />
                    </div>
                    <p className="text-xs text-tertiary tabular-nums">
                        Completed in this run: {rerunProgress.finished} / {rerunProgress.totalSteps} steps
                    </p>
                </div>
            )}

            {/* Scrollable area: email viewer + step timeline */}
            <div className="flex-1 overflow-y-auto min-h-0 pr-1">
                {/* Collapsible email viewer */}
                {pipelineStatus?.sender && (
                    <div className="mb-3">
                        <button
                            type="button"
                            className="flex items-center gap-2 text-sm text-secondary hover:text-primary transition-colors w-full"
                            onClick={() => setEmailViewerOpen((v) => !v)}
                        >
                            {emailViewerOpen
                                ? <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                                : <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                            }
                            <span className="font-medium">Email Preview</span>
                            <span className="text-tertiary truncate">
                                {pipelineStatus.sender}
                            </span>
                        </button>
                        {emailViewerOpen && (
                            <div className="mt-2 rounded-lg border border-secondary bg-secondary p-4 space-y-2 text-sm">
                                <div className="flex gap-8">
                                    <div>
                                        <span className="text-tertiary">From: </span>
                                        <span className="text-primary">{pipelineStatus.sender}</span>
                                    </div>
                                    {pipelineStatus.received_at && (
                                        <div>
                                            <span className="text-tertiary">Received: </span>
                                            <span className="text-primary">{pipelineStatus.received_at}</span>
                                        </div>
                                    )}
                                </div>
                                {pipelineStatus.subject && (
                                    <div>
                                        <span className="text-tertiary">Subject: </span>
                                        <span className="text-primary font-medium">{pipelineStatus.subject}</span>
                                    </div>
                                )}
                                {pipelineStatus.attachment_names && pipelineStatus.attachment_names.length > 0 && (
                                    <div>
                                        <span className="text-tertiary">Attachments: </span>
                                        <span className="text-primary">{pipelineStatus.attachment_names.join(", ")}</span>
                                    </div>
                                )}
                                {pipelineStatus.body_preview && (
                                    <div className="mt-2 pt-2 border-t border-tertiary">
                                        <pre className="text-xs text-secondary whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
                                            {pipelineStatus.body_preview}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {/* First-run banner: all steps are not_run */}
                {mergedSteps.length > 0 &&
                    statusCounts.not_run === mergedSteps.length &&
                    !isRunning &&
                    runMode === "idle" && (() => {
                    const emailStatus = pipelineStatus?.status;
                    const isClassifiedOnly = emailStatus === "training" || emailStatus === "irrelevant" || emailStatus === "limbo";
                    return (
                    <div className={`rounded-lg border p-4 mb-3 flex items-center gap-3 ${
                        isClassifiedOnly
                            ? "border-secondary bg-secondary"
                            : "border-brand-solid/40 bg-brand-primary_alt"
                    }`}>
                        {isClassifiedOnly
                            ? <CheckCircle className="h-5 w-5 text-tertiary shrink-0" />
                            : <AlertTriangle className="h-5 w-5 text-brand-secondary shrink-0" />
                        }
                        <div className="flex-1">
                            <p className="text-sm font-medium text-primary">
                                {isClassifiedOnly
                                    ? `Classified as ${emailStatus}`
                                    : "Email not yet processed"
                                }
                            </p>
                            <p className="text-xs text-tertiary mt-0.5">
                                {isClassifiedOnly
                                    ? `This email was classified as "${emailStatus}" — pipeline steps were skipped. You can still run them manually with Run All.`
                                    : "No pipeline steps have run for this email. Click Run All to execute the full pipeline."
                                }
                            </p>
                        </div>
                    </div>
                    );
                })()}

                <StepTimeline
                    steps={mergedSteps}
                    expandedSteps={expandedSteps}
                    onToggleStep={handleToggleStep}
                    nextStepName={nextStepName}
                    modelOverrides={modelOverrides}
                    onModelChange={handleModelChange}
                />

                {/* Error events from SSE */}
                {stepEvents
                    .filter((e) => e.event === "error")
                    .map((e, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-error-primary mt-2">
                            <XCircle className="h-4 w-4 shrink-0" />
                            {e.error}
                        </div>
                    ))}
            </div>
        </div>
    );
}

// Keep backward-compatible export name
export { StepDetailPanel as StepInspector };
